/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { AddPassengerToTripRequest } from '../models/add-passenger-to-trip-request';
import { CreateTripRequest } from '../models/create-trip-request';
import { DetailedTripResponse } from '../models/detailed-trip-response';
import { GenericResponse } from '../models/generic-response';
import { GetPassengersByTripResponse } from '../models/get-passengers-by-trip-response';
import { GetTripResponse } from '../models/get-trip-response';
import { TripResponse } from '../models/trip-response';
import { UpdateTripRequest } from '../models/update-trip-request';

/**
 * Trip apis
 */
@Injectable({ providedIn: 'root' })
export class TripService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllTrip()` */
  static readonly GetAllTripPath = '/api/v1/trips';

  /**
   * Get list of trip.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllTrip()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllTrip$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - tripNumber
       * - aircraft
       *   - tailname
       *   - type
       * - departureAirport
       *   - icao
       *   - name
       *   - city
       *   - country
       *   - iata
       * - arrivalAirport
       *   - icao
       *   - name
       *   - city
       *   - country
       *   - iata
       * - flightType
       * - trip status
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetTripResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TripService.GetAllTripPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetTripResponse>;
        })
      );
  }

  /**
   * Get list of trip.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllTrip$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllTrip(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - tripNumber
       * - aircraft
       *   - tailname
       *   - type
       * - departureAirport
       *   - icao
       *   - name
       *   - city
       *   - country
       *   - iata
       * - arrivalAirport
       *   - icao
       *   - name
       *   - city
       *   - country
       *   - iata
       * - flightType
       * - trip status
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<GetTripResponse> {
    return this.getAllTrip$Response(params, context).pipe(
      map((r: StrictHttpResponse<GetTripResponse>): GetTripResponse => r.body)
    );
  }

  /** Path part for operation `createTrip()` */
  static readonly CreateTripPath = '/api/v1/trips';

  /**
   * Create new trip.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createTrip()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTrip$Response(
    params: {
      body: CreateTripRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<TripResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TripService.CreateTripPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<TripResponse>;
        })
      );
  }

  /**
   * Create new trip.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createTrip$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTrip(
    params: {
      body: CreateTripRequest;
    },
    context?: HttpContext
  ): Observable<TripResponse> {
    return this.createTrip$Response(params, context).pipe(
      map((r: StrictHttpResponse<TripResponse>): TripResponse => r.body)
    );
  }

  /** Path part for operation `getTripDetailsById()` */
  static readonly GetTripDetailsByIdPath = '/api/v1/trips/{id}';

  /**
   * Get trip details by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getTripDetailsById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTripDetailsById$Response(
    params: {
      /**
       * The trip's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<DetailedTripResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TripService.GetTripDetailsByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<DetailedTripResponse>;
        })
      );
  }

  /**
   * Get trip details by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getTripDetailsById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTripDetailsById(
    params: {
      /**
       * The trip's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<DetailedTripResponse> {
    return this.getTripDetailsById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<DetailedTripResponse>): DetailedTripResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `deleteTrip()` */
  static readonly DeleteTripPath = '/api/v1/trips/{id}';

  /**
   * Delete trip by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteTrip()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteTrip$Response(
    params: {
      /**
       * The trip's id that needs to be deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TripService.DeleteTripPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete trip by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteTrip$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteTrip(
    params: {
      /**
       * The trip's id that needs to be deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteTrip$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateTrip()` */
  static readonly UpdateTripPath = '/api/v1/trips/{id}';

  /**
   * Update trip by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateTrip()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTrip$Response(
    params: {
      /**
       * The trip's id that needs to be updated
       */
      id: number;
      body: UpdateTripRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<TripResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TripService.UpdateTripPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<TripResponse>;
        })
      );
  }

  /**
   * Update trip by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateTrip$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTrip(
    params: {
      /**
       * The trip's id that needs to be updated
       */
      id: number;
      body: UpdateTripRequest;
    },
    context?: HttpContext
  ): Observable<TripResponse> {
    return this.updateTrip$Response(params, context).pipe(
      map((r: StrictHttpResponse<TripResponse>): TripResponse => r.body)
    );
  }

  /** Path part for operation `listPassengersByTrip()` */
  static readonly ListPassengersByTripPath = '/api/v1/trips/{id}/passengers';

  /**
   * List passengers by trip.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listPassengersByTrip()` instead.
   *
   * This method doesn't expect any request body.
   */
  listPassengersByTrip$Response(
    params: {
      /**
       * The trip's id of passengers to be listed
       */
      id: number;

      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetPassengersByTripResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TripService.ListPassengersByTripPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetPassengersByTripResponse>;
        })
      );
  }

  /**
   * List passengers by trip.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listPassengersByTrip$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listPassengersByTrip(
    params: {
      /**
       * The trip's id of passengers to be listed
       */
      id: number;

      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;
    },
    context?: HttpContext
  ): Observable<GetPassengersByTripResponse> {
    return this.listPassengersByTrip$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<GetPassengersByTripResponse>
        ): GetPassengersByTripResponse => r.body
      )
    );
  }

  /** Path part for operation `addPassengersToTrip()` */
  static readonly AddPassengersToTripPath = '/api/v1/trips/{id}/passengers';

  /**
   * Add passengers to trip.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `addPassengersToTrip()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addPassengersToTrip$Response(
    params: {
      /**
       * The trip's id of passengers to be added
       */
      id: number;
      body: AddPassengerToTripRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TripService.AddPassengersToTripPath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Add passengers to trip.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `addPassengersToTrip$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addPassengersToTrip(
    params: {
      /**
       * The trip's id of passengers to be added
       */
      id: number;
      body: AddPassengerToTripRequest;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.addPassengersToTrip$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `getTripDetailsReport()` */
  static readonly GetTripDetailsReportPath = '/api/v1/trips/{id}/report';

  /**
   * to download trip details report.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getTripDetailsReport()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTripDetailsReport$Response(
    params: {
      /**
       * The id of the trip.
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Blob>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TripService.GetTripDetailsReportPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'blob',
          accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          context,
        })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Blob>;
        })
      );
  }

  /**
   * to download trip details report.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getTripDetailsReport$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTripDetailsReport(
    params: {
      /**
       * The id of the trip.
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<Blob> {
    return this.getTripDetailsReport$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }
}
